import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DrivetrainFilterIcon } from '../../assets';
import { useFilterContext } from '../../contexts/FiltersContext';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import { DrivetrainFilter } from '../../models/Filter';
import BaseFilterComponent from './BaseFilterComponent';
import { StyledButton, StyledSecondaryButton } from './FiltersList.styles';
import { useSelectionFilter } from './hooks/SelectionFilter';
import SelectionFilter, { Value } from './SelectionFilter/SelectionFilter';
import { FilterFooterMobile } from './shared/FilterFooterMobile.styles';

export const AddDrivetrainFilterComponent = () => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { getSelectedFilter, filterCountsData, handleApplyFilter, setOpenFiltersMobile, setSelectedFilterTitle } =
    useFilterContext();

  const filter = getSelectedFilter(DrivetrainFilter) as DrivetrainFilter;
  const [arrOptions, setArrOptions] = React.useState<Value[]>(null);

  const { getFilter, optionValues, onChangeMultiSelectionFnc, clearAllSelected } = useSelectionFilter({
    arrOptions,
    filter,
    constructor: (drivetrains) => new DrivetrainFilter({ values: drivetrains }),
  });

  React.useEffect(() => {
    setArrOptions(
      filterCountsData?.drivetrain?.map((option) => ({
        selected: option.isSelected,
        value: option.key,
        label: option.key,
        data: option.count,
      })),
    );
  }, [filterCountsData]);

  const applyOnClick = () => {
    if (!getFilter) return;
    const filter = getFilter();
    handleApplyFilter(filter);
    /* This setTimeOut is because when setOpenFiltersMobile(false) all the content is unMounted,
       And some effects in useSelectionFilter are not being executed. */
    setTimeout(() => {
      setOpenFiltersMobile(false);
      setSelectedFilterTitle('');
    }, 100);
  };

  const handleClearAll = () => {
    return clearAllSelected();
  };

  return (
    <>
      <SelectionFilter
        values={optionValues}
        onChange={onChangeMultiSelectionFnc}
        multiselect
        displayCount={!inMobile(width)}
        reverseCheckboxOrder={inMobile(width)}
        checkboxSizeInPx={inMobile(width) ? 24 : undefined}
      />
      <FilterFooterMobile>
        <StyledSecondaryButton onPress={handleClearAll}>{t('filters.clearAll')}</StyledSecondaryButton>
        <StyledButton disabled={arrOptions?.length === 0} onPress={applyOnClick}>
          {t('filters.applyFilter')}
        </StyledButton>
      </FilterFooterMobile>
    </>
  );
};

const AddDrivetrainFilter = () => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { getSelectedFilter, filterCountsData } = useFilterContext();

  const filter = getSelectedFilter(DrivetrainFilter) as DrivetrainFilter;
  const [arrOptions, setArrOptions] = React.useState<Value[]>(null);

  const { getFilter, optionValues, onCancel, onChangeMultiSelectionFnc, clearAllSelected } = useSelectionFilter({
    arrOptions,
    filter,
    constructor: (drivetrains) => new DrivetrainFilter({ values: drivetrains }),
  });

  React.useEffect(() => {
    setArrOptions(
      filterCountsData?.drivetrain?.map((option) => ({
        selected: option.isSelected,
        value: option.key,
        label: option.key,
        data: option.count,
      })),
    );
  }, [filterCountsData]);

  return (
    <BaseFilterComponent
      getFilter={getFilter}
      icon={<DrivetrainFilterIcon />}
      title={t('filters.drivetrain')}
      onCancel={onCancel}
      onClearAll={clearAllSelected}
      inUse={!!filter}
      storedFilter={filter}
      disableFilterButton={arrOptions?.length === 0}
    >
      <SelectionFilter
        values={optionValues}
        onChange={onChangeMultiSelectionFnc}
        multiselect
        displayCount={!inMobile(width)}
        reverseCheckboxOrder={inMobile(width)}
        checkboxSizeInPx={inMobile(width) ? 24 : undefined}
      />
    </BaseFilterComponent>
  );
};

export default AddDrivetrainFilter;
